import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Menu, X, LogOut, Settings, MessageCircle, Home, Search, Shield, User } from 'lucide-react';
import useRuolo from '../hooks/useRuolo';
import { useProfilo } from '../context/ProfiloContext';
import './Navbar.css';

function Navbar() {
  const { isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const { ruolo } = useRuolo();
  const { profilo } = useProfilo();
  // Avatar caricato dal fornitore (DB) con fallback all'immagine di Clerk.
  const avatarSrc = profilo?.avatar_url || user?.profileImageUrl || '/default-avatar.svg';
  const isAdmin = ruolo === 'admin';
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadMessages] = useState(0);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setDropdownOpen(false);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="Feste Da Piccoli" className="navbar-logo-img" />
          <span className="logo-text">Feste Da Piccoli</span>
        </Link>

        {/* Desktop Menu */}
        <ul className="nav-menu">
          <li className="nav-item">
            <Link to="/" className="nav-link">
              Home
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/cerca?tipo=location" className="nav-link">
              Esplora
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/blog" className="nav-link">
              Blog
            </Link>
          </li>
          {!isSignedIn && (
            <li className="nav-item">
              <Link to="/guida" className="nav-link">
                Guida
              </Link>
            </li>
          )}

          {isSignedIn && isAdmin && (
            <li className="nav-item">
              <Link to="/admin" className="nav-link">
                <Shield size={18} />
                Pannello Admin
              </Link>
            </li>
          )}

          {isSignedIn && !isAdmin && (
            <>
              <li className="nav-item">
                <Link to="/dashboard" className="nav-link">
                  Dashboard
                </Link>
              </li>
              <li className="nav-item relative">
                <Link to="/dashboard/messaggi" className="nav-link">
                  <MessageCircle size={18} />
                  Messaggi
                  {unreadMessages > 0 && (
                    <span className="badge-unread">{unreadMessages}</span>
                  )}
                </Link>
              </li>
            </>
          )}
        </ul>

        {/* Right Side */}
        <div className="navbar-right">
          {!isSignedIn ? (
            <button
              className="btn-register"
              onClick={() => navigate('/registrazione')}
            >
              Area fornitori
            </button>
          ) : (
            <div className="user-dropdown">
              <button
                className="btn-avatar"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <img
                  src={avatarSrc}
                  alt="Avatar"
                  className="avatar-img"
                  onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.svg'; }}
                />
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu">
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      className="dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Shield size={16} />
                      Pannello Admin
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/dashboard"
                        className="dropdown-item"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <Settings size={16} />
                        Dashboard
                      </Link>
                      <Link
                        to="/dashboard/profilo"
                        className="dropdown-item"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <User size={16} />
                        Il mio profilo
                      </Link>
                      <Link
                        to="/dashboard/messaggi"
                        className="dropdown-item"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <MessageCircle size={16} />
                        Messaggi
                      </Link>
                    </>
                  )}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item logout-btn" onClick={handleSignOut}>
                    <LogOut size={16} />
                    Esci
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="mobile-menu">
          <Link
            to="/"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            <Home size={18} />
            Home
          </Link>
          <Link
            to="/cerca?tipo=location"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            <Search size={18} />
            Esplora
          </Link>
          <Link
            to="/blog"
            className="mobile-menu-link"
            onClick={() => setMenuOpen(false)}
          >
            Blog
          </Link>
          {!isSignedIn && (
            <Link
              to="/guida"
              className="mobile-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              Guida
            </Link>
          )}

          {isSignedIn && isAdmin && (
            <Link
              to="/admin"
              className="mobile-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              <Shield size={18} />
              Pannello Admin
            </Link>
          )}

          {isSignedIn && !isAdmin && (
            <>
              <Link
                to="/dashboard"
                className="mobile-menu-link"
                onClick={() => setMenuOpen(false)}
              >
                <Settings size={18} />
                Dashboard
              </Link>
              <Link
                to="/dashboard/profilo"
                className="mobile-menu-link"
                onClick={() => setMenuOpen(false)}
              >
                <User size={18} />
                Il mio profilo
              </Link>
              <Link
                to="/dashboard/messaggi"
                className="mobile-menu-link"
                onClick={() => setMenuOpen(false)}
              >
                <MessageCircle size={18} />
                Messaggi
              </Link>
            </>
          )}

          {isSignedIn && (
            <>
              <button
                className="mobile-menu-link logout"
                onClick={() => {
                  handleSignOut();
                  setMenuOpen(false);
                }}
              >
                <LogOut size={18} />
                Esci
              </button>
            </>
          )}

          {!isSignedIn && (
            <button
              className="mobile-menu-link register"
              onClick={() => {
                navigate('/registrazione');
                setMenuOpen(false);
              }}
            >
              Area fornitori
            </button>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;
